/*!
 * Copyright © 2020-2021. Verizon Connect Ireland Limited. All rights reserved.
 */

import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { select, Store } from '@ngrx/store';
import moment from 'moment';
import { forkJoin, Observable } from 'rxjs';
import { filter, map, mapTo, take, tap } from 'rxjs/operators';
import { isElementNull, isStringNullOrEmpty, NumberUtilities } from '../../../../utils';
import { FEATURE_TOGGLES } from '../../constants';
import { EViewReplayType, IViewReplayParameters } from '../../models';
import {
  CloseInsightsPanel,
  DisableFeatureToggle,
  EnableFeatureToggle,
  GetAccessibleVehiclesCountSuccess,
  GetVehiclePlotsForUserWithMetadata,
  RemoveIndexLoaderIcon,
  TriggerViewReplay,
  UpdateAssetAsSelected,
  UpdateIsAppAnimationsEnabled,
  UpdateIsFullscreen,
  UpdateVehicleAsSelected
} from '../../store/actions';
import {
  getAssetPlots,
  getIsFullscreen,
  getMapHierarchyInfo,
  getMapModuleLoaded,
  getUserPermissions,
  getUserSettings,
  getVehiclePlots
} from '../../store/selectors';
import { IAppState } from '../../store/state';
import { IAppLauncherService } from './app-launcher.service.interface';
import { IReplayRouteParams } from './replay-route-params.interface';

@Injectable({
  providedIn: 'root'
})
export class AppLauncherService implements IAppLauncherService {
  launchReplay(routeParams: IReplayRouteParams): void {
    if (!this._validateReplayRouteParams(routeParams)) {
      this._document.defaultView.location.href = '/';
    }

    this._dispatchReplayActions(routeParams);
  }

  overrideFeatureToggles(featuresToEnable: string[], featuresToDisable: string[]): void {
    // create a map of available feature toggles, with lower case key
    const availableFeatureToggles = new Map(Object.values(FEATURE_TOGGLES).map(value => [value.toLowerCase(), value]));
    // ensure that featre toggle from query parameter is valid
    const togglesToEnable = featuresToEnable?.filter(t => availableFeatureToggles.has(t)).map(t => availableFeatureToggles.get(t));
    const togglesToDisable = featuresToDisable?.filter(t => availableFeatureToggles.has(t)).map(t => availableFeatureToggles.get(t));

    this._store
      .pipe(
        select(getUserPermissions),
        // wait until the user permissions are loaded - at this point we know that feature toggles from UIS are loaded
        filter(userPermissions => !isElementNull(userPermissions), take(1)),
        tap(() => {
          this._store.dispatch(new EnableFeatureToggle(togglesToEnable));
          this._store.dispatch(new DisableFeatureToggle(togglesToDisable));
        })
      )
      .subscribe();
  }

  launchTextView(): void {
    if (isPlatformBrowser(this._platformId)) {
      this._store.dispatch(new RemoveIndexLoaderIcon());
      this._store.dispatch(new UpdateIsAppAnimationsEnabled(false));
      this._enableBasicFunctionality();
      this._store.dispatch(new GetVehiclePlotsForUserWithMetadata());
    }
  }

  constructor(
    @Inject(PLATFORM_ID) private readonly _platformId: object,
    @Inject(DOCUMENT) private readonly _document: Document,
    private readonly _store: Store<IAppState>
  ) {}

  private _validateReplayRouteParams(routeParams: IReplayRouteParams): boolean {
    return (!isElementNull(routeParams.vehicleId) || !isElementNull(routeParams.nonPoweredAssetId)) && !isElementNull(routeParams.start);
  }

  private _dispatchReplayActions(routeParams: IReplayRouteParams): void {
    this._store.dispatch(new CloseInsightsPanel());
    if (isPlatformServer(this._platformId)) {
      this._dispatchReplayActionsSSR();
    }
    if (isPlatformBrowser(this._platformId)) {
      this._dispatchReplayActionsBrowser(routeParams);
    }
  }

  private _dispatchReplayActionsSSR() {
    this._store.dispatch(new UpdateIsFullscreen(true));
  }

  private _dispatchReplayActionsBrowser(routeParams: IReplayRouteParams): void {
    this._enableBasicFunctionality();

    this._updateSelection(routeParams);

    this._waitForReplayDataToLoad(routeParams).subscribe(() => this._triggerReplay(routeParams));
  }

  private _enableBasicFunctionality(): void {
    /* actions to initialize the map:
    GetAccessibleVehiclesCountSuccess is needed for scoping. since we don't have the selection tree
    in replay mode, the value doesn't matter
    */
    this._enableFullscreenIfNotEnabled();
    this._store.dispatch(new GetAccessibleVehiclesCountSuccess(0));
  }

  private _enableFullscreenIfNotEnabled(): void {
    this._store
      .pipe(
        select(getIsFullscreen),
        take(1),
        filter(isFullsreen => !isFullsreen)
      )
      .subscribe(() => this._store.dispatch(new UpdateIsFullscreen(true)));
  }

  private _waitForReplayDataToLoad(routeParams: IReplayRouteParams): Observable<void> {
    const isPlotDataLoaded$: Observable<void> = routeParams.nonPoweredAssetId
      ? this._store.pipe(
          select(getAssetPlots),
          map(plots => plots.find(p => p.id === +routeParams.nonPoweredAssetId)),
          filter(p => !isElementNull(p)),
          take(1),
          mapTo(void 0)
        )
      : this._store.pipe(
          select(getVehiclePlots),
          map(plots => plots.find(p => p.id === +routeParams.vehicleId)),
          filter(p => !isElementNull(p)),
          take(1),
          mapTo(void 0)
        );

    const isMapLoadedTrue$: Observable<void> = this._store.pipe(
      select(getMapModuleLoaded),
      filter(isLoaded => isLoaded),
      take(1),
      mapTo(void 0)
    );
    const isUserSettingsLoaded$: Observable<void> = this._store.pipe(
      select(getUserSettings),
      filter(u => !isElementNull(u)),
      take(1),
      mapTo(void 0)
    );
    const streams = [isPlotDataLoaded$, isMapLoadedTrue$, isUserSettingsLoaded$];
    return forkJoin(streams).pipe(mapTo(void 0));
  }

  private _updateSelection(routeParams: IReplayRouteParams): void {
    const whenMapHierarchyIsLoaded$ = this._store.pipe(
      select(getMapHierarchyInfo),
      filter(h => h.mapHierarchy.length > 0),
      take(1),
      mapTo(void 0)
    );

    // we need to make sure the vehicle or asset is selected, so we have its plot data loaded in the store
    whenMapHierarchyIsLoaded$.subscribe(() => {
      if (!!routeParams.nonPoweredAssetId) {
        this._store.dispatch(
          new UpdateAssetAsSelected({
            focus: false,
            selectedEntity: `a${routeParams.nonPoweredAssetId}`
          })
        );
      } else {
        this._store.dispatch(
          new UpdateVehicleAsSelected({
            focus: false,
            selectedEntity: `v${routeParams.vehicleId}`
          })
        );
      }
    });
  }

  private _triggerReplay(routeParams: IReplayRouteParams): void {
    const startDateUtc = moment.utc(routeParams.start);
    const preselectSegmentTimeUtc = isStringNullOrEmpty(routeParams.preselectSegmentTime)
      ? null
      : moment.utc(routeParams.preselectSegmentTime);

    const triggerReplayParameters: IViewReplayParameters = routeParams.nonPoweredAssetId
      ? {
          id: +routeParams.nonPoweredAssetId,
          type: EViewReplayType.Asset,
          dateTime: startDateUtc.toDate(),
          disableAnimation: true, // disable animation for faster loading time
          onCloseNavigateToHome: true, // define behavior for close replay button
          preselectSegmentTime: preselectSegmentTimeUtc?.toDate(),
          isOpenedFromExternal: true
        }
      : {
          id: +routeParams.vehicleId,
          type: EViewReplayType.Vehicle,
          dateTime: startDateUtc.toDate(),
          disableAnimation: true, // disable animation for faster loading time
          onCloseNavigateToHome: true, // define behavior for close replay button
          accountSettingOverrides: {
            minimumStopDuration: NumberUtilities.tryParseInt(routeParams.minimumStopDuration),
            minimumIdleDuration: NumberUtilities.tryParseInt(routeParams.minimumIdleDuration),
            showIdleSegments: isStringNullOrEmpty(routeParams.showIdleSegments) ? null : routeParams.showIdleSegments === 'true'
          },
          preselectSegmentTime: preselectSegmentTimeUtc?.toDate(),
          isOpenedFromExternal: true
        };

    this._store.dispatch(new TriggerViewReplay(triggerReplayParameters));
  }
}
